// kataEhrungAnim.js — Takt und Effekte für Kata-Tafel und Siegerehrung
// (20.10.2026, Vorlage docs/muster-animationen/muster.html).
//
// Die Zeiten im Muster sind die ursprünglichen; abgenommen wurde das Muster
// mit einem Tempofaktor (TEMPO_KATA, TEMPO_EHRUNG). Hier wird derselbe Faktor
// auf Dauer, Verzögerung und Wartezeit gelegt, damit die Zahlen aus dem Muster
// 1:1 übernommen werden können.
//
// Web Animations API, keine Fremdbibliothek. Bei prefers-reduced-motion
// laufen keine Animationen: die Seiten zeigen gleich den Endstand.
import React from 'react';
import './kataEhrungAnim.css';
import { hallenFoto } from '../../lib/hallenbild';

export const TEMPO_KATA = 1.6;
export const TEMPO_EHRUNG = 1.6;

export const ohneBewegung = () => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

/** Element.animate mit Tempofaktor (Dauer und Verzögerung). */
export function animiere(el, frames, opt, tempo) {
  if (!el || typeof el.animate !== 'function') return null;
  const o = typeof opt === 'number'
    ? opt * tempo
    : { ...opt, duration: (opt.duration || 0) * tempo, delay: (opt.delay || 0) * tempo };
  return el.animate(frames, o);
}

/** Wartet ms * tempo; bricht ab (resolve false), wenn lauf() nicht mehr gilt. */
export const warte = (ms, tempo) => new Promise((r) => setTimeout(r, ms * tempo));

/** Lage eines Elements relativ zur Bühne. */
const relativ = (buehne, el) => {
  const t = buehne.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  return { x: r.left - t.left, y: r.top - t.top, w: r.width, h: r.height };
};

/**
 * Goldene Zahl fegt schräg in ein Feld, am Ziel ein Lichtblitz (Muster:
 * zahlFegt). Der Text im Feld selbst wird vom Aufrufer gesetzt.
 */
export async function zahlFegt(buehne, fx, feld, text, groesse, tempo) {
  if (!buehne || !fx || !feld) return;
  const ziel = relativ(buehne, feld);
  const cq = buehne.getBoundingClientRect().width / 100;
  const g = document.createElement('div');
  g.className = 'kea-wisch';
  g.textContent = text;
  g.style.fontSize = `${ziel.h * groesse}px`;
  fx.appendChild(g);
  const gw = g.getBoundingClientRect();
  const zx = ziel.x + ziel.w / 2 - gw.width / 2;
  const zy = ziel.y + ziel.h / 2 - gw.height / 2;
  animiere(g, [
    { transform: `translate(${zx - 22 * cq}px, ${zy - 14 * cq}px) rotate(-14deg) scale(1.2) skewX(-12deg)`, opacity: 0, filter: 'blur(6px)' },
    { opacity: 1, offset: 0.15 },
    { transform: `translate(${zx}px, ${zy}px) rotate(0) scale(.3)`, opacity: 1, filter: 'blur(0)', offset: 0.7 },
    { transform: `translate(${zx}px, ${zy}px) scale(.2)`, opacity: 0 },
  ], { duration: 520, easing: 'cubic-bezier(.3,.6,.2,1)', fill: 'both' }, tempo);
  await warte(360, tempo);
  const b = document.createElement('div');
  b.className = 'kea-blitz';
  fx.appendChild(b);
  const bg = ziel.h * 2;
  Object.assign(b.style, {
    width: `${bg}px`, height: `${bg}px`,
    left: `${ziel.x + ziel.w / 2 - bg / 2}px`, top: `${ziel.y + ziel.h / 2 - bg / 2}px`,
  });
  animiere(b, [{ transform: 'scale(.3)', opacity: 1 }, { transform: 'scale(1.2)', opacity: 0 }],
    { duration: 480, easing: 'ease-out', fill: 'both' }, tempo);
  setTimeout(() => { g.remove(); b.remove(); }, 900 * tempo);
}

// Medaille aus dem Muster (Gold, Silber, Bronze), als SVG statt Emoji.
const MEDAILLE = {
  1: ['#fff1b8', '#e8c25a', '#9a6a12'],
  2: ['#ffffff', '#c9ced6', '#7c8592'],
  3: ['#ffd2a8', '#c7814a', '#6e3e19'],
};
export function Medaille({ platz, className = '' }) {
  const farben = MEDAILLE[platz];
  if (!farben) return null;
  const [a, b, c] = farben;
  const id = `kea-m${platz}`;
  return (
    <svg className={className} viewBox="0 0 40 40" aria-hidden>
      <defs>
        <radialGradient id={id} cx=".35" cy=".3" r=".8">
          <stop offset="0" stopColor={a} /><stop offset=".55" stopColor={b} /><stop offset="1" stopColor={c} />
        </radialGradient>
      </defs>
      <circle cx="20" cy="20" r="18" fill={`url(#${id})`} stroke={c} strokeWidth="1.5" />
      <circle cx="20" cy="20" r="13.5" fill="none" stroke={a} strokeOpacity=".7" strokeWidth="1" />
      <text x="20" y="26.5" textAnchor="middle" fontFamily="Georgia" fontStyle="italic" fontWeight="900" fontSize="18" fill={c}>{platz}</text>
    </svg>
  );
}

/**
 * Bild eines Starters: Foto → Vereinslogo → Turnierlogo/TDA-Logo (Reihenfolge
 * vorgegeben). Gibt Adresse und Art zurück; die Art steuert den Zuschnitt
 * (Foto füllt, Vereinslogo mit Rand, das TDA-Logo wie im Muster beschnitten).
 * Foto seit 21.10.2026 nur mit Hallenschlüssel des Bildschirms (halle =
 * { turnierId, k }) und Einwilligung (person.foto_frei), über /api/hallenbild
 * (lib/hallenbild.js). Ein Pfad in person.foto zählt nicht mehr.
 */
export function starterBild(person, ersatz, halle = null) {
  const foto = hallenFoto(person, halle);
  if (foto) return { url: foto, art: 'foto' };
  if (person?.verein_logo) return { url: person.verein_logo, art: 'verein' };
  const logo = ersatz || '/logo.png';
  return { url: logo, art: logo === '/logo.png' ? 'logo' : 'verein' };
}

/** CSS-Wert für background-image (Anführungszeichen im Pfad entschärft). */
export const bildUrl = (url) => `url("${String(url || '').replace(/["\\\n]/g, '')}")`;

/** Punktzahl so, wie die API sie liefert: mindestens eine, höchstens zwei Nachkommastellen. */
export function punkteText(wert) {
  if (wert === null || wert === undefined || wert === '') return '–';
  const n = Number(wert);
  if (!Number.isFinite(n)) return String(wert);
  const zwei = n.toFixed(2);
  return zwei.endsWith('0') ? n.toFixed(1) : zwei;
}
